import { v4 as uuidv4 } from 'uuid';
import { QuestionPool, QuestionSingle } from './types';

/**
 * Stable, editor-only identity for a conditional child question.
 *
 * Conditional children live in a plain array with no id of their own, and deleting one splices that
 * array, so `parent|condition|index` names a slot rather than a question: whatever component is
 * mounted at an unchanged index silently starts editing the child that moved into it. The child's
 * text is no better, because two children can carry the same text and editing one child's text
 * looks exactly like a replacement. A key minted when the child enters the editor rides the object
 * through every splice, reorder and spread, which is the only thing that makes per-child editor
 * state safe to key on.
 *
 * It is editor bookkeeping and never part of a questionnaire. `withoutEditorKeys` takes it back off
 * on the way to the API, and `QuestionSingle.__editorKey` is documented as editor-only.
 */
export const createEditorKey = (): string => uuidv4();

/**
 * Gives every conditional child in the pool an editor key, in place.
 *
 * In place on purpose: the key is only worth something while it rides the same object the editor
 * already holds, so replacing objects here would defeat it. This runs on every `SET_QUESTIONS`,
 * which is the one point every path funnels through - loading a saved questionnaire, adding a
 * child, duplicating a child, duplicating a parent, adding a multiple-choice option - so no
 * creation site has to remember to mint one.
 *
 * A key already seen in this pass is re-minted rather than kept, because `cloneDeep` is how both
 * duplicate actions work and a copied key would otherwise name two different children at once.
 *
 * Sameness is by object identity, not by key value, and that distinction is the whole safety of
 * this function. Several handlers write the parent under the child's numeric index rather than the
 * parent's pool id, which leaves the pool holding the same parent twice; integer-like keys iterate
 * first, so the second visit would see every key already taken and re-mint the lot on every single
 * dispatch. The call sites are fixed, but a caller must not be able to reshuffle identities by
 * getting a pool key wrong: reaching the same child object twice is an alias, never a duplicate.
 */
export const assignEditorKeys = (questions: QuestionPool): QuestionPool => {
	const visited = new WeakSet<QuestionSingle>();
	const keysInUse = new Set<string>();

	Object.keys(questions).forEach((questionId) => {
		const conditions = questions[questionId]?.conditionalQuestion?.conditions;
		if (!conditions) {
			return;
		}

		Object.keys(conditions).forEach((condition) => {
			(conditions[condition] || []).forEach((child: QuestionSingle) => {
				if (visited.has(child)) {
					return;
				}
				visited.add(child);

				if (!child.__editorKey || keysInUse.has(child.__editorKey)) {
					child.__editorKey = createEditorKey();
				}

				keysInUse.add(child.__editorKey);
			});
		});
	});

	return questions;
};

/**
 * A copy of a question, and of its conditional children, with the editor keys taken back off.
 *
 * A copy rather than a delete in place, because the editor goes on holding the same questionnaire
 * after a save and the keys have to survive it. Nothing else is added, removed or reordered, so a
 * questionnaire nobody edited serialises byte for byte as it arrived.
 */
export const withoutEditorKeys = (question: QuestionSingle): QuestionSingle => {
	const stripped: QuestionSingle = { ...question };
	delete stripped.__editorKey;

	const conditionalQuestion = stripped.conditionalQuestion;
	const conditions = conditionalQuestion?.conditions;
	if (!conditionalQuestion || !conditions) {
		return stripped;
	}

	const strippedConditions: { [condition: string]: QuestionSingle[] } = {};
	Object.keys(conditions).forEach((condition) => {
		strippedConditions[condition] = (conditions[condition] || []).map((child: QuestionSingle) =>
			withoutEditorKeys(child),
		);
	});

	stripped.conditionalQuestion = { ...conditionalQuestion, conditions: strippedConditions };
	return stripped;
};
